import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Avatar, Div, Span } from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'

import ActivityActions, { ActionType } from 'components/ActivityActions'
import Card from 'components/Card'
import Paginator from 'components/Paginator'
import {
  driftEmptyStateBuilder,
  driftErrorStateBuilder,
  driftLoadingStateBuilder
} from 'components/States'
import { SortContext } from 'context/sortContext'
import { useAccountContactActivity } from 'hooks/useAccountContactActivity'
import {
  AccountActivitySortValue,
  CompanyData,
  ContactWithActivites
} from 'modules/accountActivity/domain'
import SortDescription from 'pages/CCRMAccountDetailsPage/ContactsTab/SortDescription'
import { getDisplayStringWithFallback, getEndUserDisplayLocation } from 'utils/endUser'
import { formatNumber } from 'utils/formatNumbers'
import { accountPath, contactPath } from 'utils/paths'
import { humanizedRelativeTime } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

const DEFAULT_PAGE_SIZE = 10
interface ContactsTabProps {
  accountId: string
  accountName?: string
}

export const role = (contactActivity: ContactWithActivites) => (
  <Span>{getDisplayStringWithFallback(contactActivity.endUserData.role)}</Span>
)

export const location = (contactActivity: ContactWithActivites) => (
  <Span>
    {getEndUserDisplayLocation(
      contactActivity.endUserData.locationCity,
      contactActivity.endUserData.locationStateCode,
      contactActivity.endUserData.locationCountryCode
    )}
  </Span>
)

export const numberOfActivities = (contactActivity: ContactWithActivites) => (
  <Span>{formatNumber(contactActivity.activityCount)}</Span>
)

export const contactInfo = (contactActivity: ContactWithActivites) => {
  const avatarCompatibleEndUser = { ...contactActivity.endUserData, type: 'END_USER' }
  return (
    <div className={styles.contactInfo}>
      <Avatar
        user={avatarCompatibleEndUser}
        withPresence={false}
        className={styles.avatarContainer}
      />
      {endUserDisplayNameWithEmail(
        contactActivity.endUserData.name,
        contactActivity.endUserData.email
      )}
    </div>
  )
}

export const endUserDisplayNameWithEmail = (displayName?: string, email?: string) => {
  if (
    typeof displayName !== 'undefined' &&
    displayName !== '' &&
    displayName !== null &&
    typeof email !== 'undefined' &&
    email !== null
  ) {
    return (
      <Div className={styles.endUserDisplayNameAndEmail}>
        <Span className={styles.endUserDisplayName}>{displayName}</Span>
        <Span className={styles.endUserDisplayEmail}>{getDisplayStringWithFallback(email)}</Span>
      </Div>
    )
  } else if (typeof email !== 'undefined' && email !== null) {
    return (
      <Div className={styles.endUserDisplayNameAndEmail}>
        <Span className={styles.endUserDisplayName}>{getDisplayStringWithFallback(email)}</Span>
      </Div>
    )
  } else {
    return (
      <Div className={styles.endUserDisplayNameAndEmail}>
        <Span className={styles.endUserDisplayName}>Site visitor</Span>
      </Div>
    )
  }
}

export const lastActive = (companyData?: CompanyData) => (
  contactActivity: ContactWithActivites
) => {
  return (
    <Div className={styles.lastActiveWithActivityActions}>
      <ActivityActions
        className="activity-actions"
        endUserData={contactActivity.endUserData}
        companyData={companyData}
        actionTypes={[
          ActionType.EMAIL,
          ActionType.LINKEDIN,
          ActionType.OUTREACH,
          ActionType.SALESLOFT,
          ActionType.CONTACT_INFO
        ]}
      />
      <Span className="contact-info-last-active">
        {humanizedRelativeTime(contactActivity.timeOfLastEngagement)}
      </Span>
    </Div>
  )
}

const contactTableHeaders = [
  {
    label: 'Contact',
    sortable: true,
    sortValue: AccountActivitySortValue.CONTACT_NAME,
    initialDescending: false,
    width: 276,
    className: styles.contactInfoHeader
  },
  {
    label: 'Role',
    sortable: true,
    sortValue: AccountActivitySortValue.CONTACT_ROLE,
    initialDescending: false
  },
  {
    label: 'Engage AI score',
    sortable: false
  },
  {
    label: 'Location',
    sortable: true,
    sortValue: AccountActivitySortValue.LOCATION,
    initialDescending: false,
    width: 132
  },
  {
    label: '# activities',
    sortable: true,
    sortValue: AccountActivitySortValue.NUM_ACTIVITIES,
    width: 132
  },
  {
    label: 'Last active',
    sortable: true,
    sortValue: AccountActivitySortValue.MOST_RECENT,
    width: 132
  }
]

const ErrorComponent = driftErrorStateBuilder("this account's contacts")
const LoadingComponent = driftLoadingStateBuilder('Loading contacts')
export const EmptyContactsComponent = driftEmptyStateBuilder(
  'No contacts yet',
  'Once a contact visits your site, they will show up here.'
)

const ContactsTab: React.FC<ContactsTabProps> = ({ accountId, accountName }) => {
  const history = useHistory()
  const [offset, setOffset] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  const { contactsSort, contactsDescending, setContactsDescending, setContactsSort } = useContext(
    SortContext
  )

  const {
    response: accountContacts,
    count,
    isLoading,
    accountContactsError
  } = useAccountContactActivity(accountId, pageSize, offset / pageSize + 1)

  const handleRowClick = (contact: ContactWithActivites) => {
    segmentTrack(TrackType.ACCOUNT_DETAILS_CONTACT_ROW_CLICK)
    history.push({
      pathname: contactPath(contact.endUserData.id),
      state: {
        navigatedFrom: accountName ?? 'Account details',
        navigatedFromUrl: accountPath(accountId)
      }
    })
  }

  return (
    <Card className={styles.contactsCard}>
      {isLoading && <LoadingComponent />}
      {!isLoading && accountContactsError && <ErrorComponent />}
      {!isLoading && (!accountContacts || count === 0) && <EmptyContactsComponent />}
      {!isLoading && !accountContactsError && accountContacts && accountContacts?.data.length > 0 && (
        <>
          <SortDescription
            count={accountContacts?.data.length || 0}
            sort={contactsSort}
            descending={contactsDescending}
          />
          <div className={styles.contactsTable}>
            <Table
              columns={contactTableHeaders.map(header => ({
                ...header,
                sorting: header.sortValue === contactsSort,
                onClick: () => {
                  if (header.sortValue) {
                    setContactsDescending(
                      header.sortValue === contactsSort
                        ? !contactsDescending
                        : header.initialDescending !== false
                    )
                    if (header.sortValue) {
                      setContactsSort(header.sortValue)
                    }
                  }
                },
                direction: contactsDescending ? 'desc' : 'asc'
              }))}
              data={
                accountContacts?.data.map(contact => ({
                  rowClickEventData: contact,
                  data: [
                    {
                      content: contactInfo(contact),
                      className: styles.contactInfoWrapper
                    },
                    {
                      content: role(contact)
                    },
                    {
                      content: contact.liftAIScore || '-'
                    },
                    {
                      content: location(contact)
                    },
                    {
                      content: numberOfActivities(contact)
                    },
                    {
                      content: lastActive()(contact),
                      className: styles.lastActive
                    }
                  ]
                })) || []
              }
              onRowClick={handleRowClick}
            />
          </div>
          <div className={styles.paginatorContainer}>
            <Paginator
              totalItems={count || 0}
              offset={offset}
              pageSize={pageSize}
              goForward={() => setOffset(offset + pageSize)}
              goBackward={() => setOffset(Math.max(offset - pageSize, 0))}
              changeSize={setPageSize}
            />
          </div>
        </>
      )}
    </Card>
  )
}

export default ContactsTab
